import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, siteInfo } from '@/mocks/articles';
import { generateArticleSchema, generateBreadcrumbSchema, generateWebPageSchema } from '@/components/feature/StructuredData';

const categoryNameMap: Record<string, string> = {
  celebrity: '明星黑料',
  influencer: '网红内幕',
  kol: '大V爆料',
  society: '社会热点',
  entertainment: '娱乐圈',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章不存在</h1>
            <p className="text-foreground-500 mb-4">您访问的文章可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-block px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryName = categoryNameMap[article.category] || article.category;
  const canonicalUrl = `https://${siteInfo.domain}/article/${article.id}`;
  const pageTitle = article.title;

  const articleLD = generateArticleSchema(
    article,
    canonicalUrl,
    article.imageUrl
  );

  const breadcrumbLD = generateBreadcrumbSchema([
    { name: '首页', url: `https://${siteInfo.domain}` },
    { name: categoryName, url: `https://${siteInfo.domain}/category/${article.category}` },
    { name: article.title, url: canonicalUrl },
  ]);

  return (
    <>
      <SEOHead
        title={`${pageTitle} - 黑料91`}
        description={article.excerpt}
        keywords={`黑料91,${article.tags.join(',')},${categoryName}爆料`}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={article.imageUrl}
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLD) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLD) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <nav className="w-full max-w-4xl mx-auto px-4 md:px-6 py-3 text-sm text-foreground-500" aria-label="面包屑导航">
            <ol className="flex items-center gap-1.5 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
              <li>/</li>
              <li><Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors">{categoryName}</Link></li>
              <li>/</li>
              <li className="text-foreground-950 font-medium truncate max-w-[200px]">{article.title}</li>
            </ol>
          </nav>

          <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-4">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="bg-primary-100 text-primary-700 text-xs font-medium px-3 py-1 rounded-full">
                  {categoryName}
                </span>
                {article.isHot && (
                  <span className="bg-accent-100 text-accent-700 text-xs font-medium px-3 py-1 rounded-full">
                    热门
                  </span>
                )}
                {article.isTop && (
                  <span className="bg-secondary-100 text-secondary-700 text-xs font-medium px-3 py-1 rounded-full">
                    置顶
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6">
                <span>发布时间: {article.date}</span>
                <div className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  <span>{(article.views / 10000).toFixed(1)}万阅读</span>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full rounded-lg object-cover max-h-[500px]"
                title={`${article.title} - 黑料91`}
              />
            </div>

            <div className="prose prose-lg max-w-none">
              {article.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-foreground-800 leading-relaxed mb-4 text-base md:text-lg">
                  {paragraph.trim()}
                </p>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2 mb-6">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-background-100 text-foreground-600 text-xs px-3 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-background-100/70 rounded-lg">
                <p className="text-sm text-foreground-600">
                  本文由<strong>黑料91</strong>原创发布，转载请联系授权。
                </p>
                <Link
                  to="/"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                >
                  &larr; 返回首页查看更多爆料
                </Link>
              </div>
            </div>

            <div className="mt-10">
              <h4 className="text-lg font-heading font-bold text-foreground-950 mb-4">相关推荐</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {articles
                  .filter((a) => a.category === article.category && a.id !== article.id)
                  .slice(0, 3)
                  .map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group cursor-pointer bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300/50 transition-all duration-300"
                    >
                      <div className="h-40 overflow-hidden">
                        <img
                          src={related.imageUrl}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h5 className="text-sm font-semibold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">
                          {related.title}
                        </h5>
                        <span className="text-xs text-foreground-400 mt-2 block">{related.date}</span>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}